import { useCallback, useEffect, useState } from 'react'
import { login as apiLogin, verifyToken } from '@/content'

const STORAGE_KEY = 'kris-admin-token'

interface AdminSession {
  token: string | null
  email: string | null
  /** True until the stored token has been checked against the API. */
  isChecking: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
}

/**
 * Holds the admin's bearer token.
 *
 * `localStorage` is used deliberately and only for this: it is a token the
 * server issued, scoped to one browser, and re-validated against `/api/auth/me`
 * on every load — a stale or revoked one signs the user straight back out.
 * Every storage access is guarded, since it throws in private windows.
 */
export const useAdminSession = (): AdminSession => {
  const [token, setToken] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    let stored: string | null = null
    try {
      stored = localStorage.getItem(STORAGE_KEY)
    } catch {
      // Storage unavailable: treat as signed out.
    }
    if (!stored) {
      setIsChecking(false)
      return
    }

    let active = true
    verifyToken(stored)
      .then((valid) => {
        if (!active) return
        if (valid) {
          setToken(stored)
        } else {
          try {
            localStorage.removeItem(STORAGE_KEY)
          } catch {
            // Nothing to do.
          }
        }
      })
      .finally(() => {
        if (active) setIsChecking(false)
      })
    return () => {
      active = false
    }
  }, [])

  const signIn = useCallback(async (address: string, password: string) => {
    const result = await apiLogin(address, password)
    setToken(result.token)
    setEmail(result.email)
    try {
      localStorage.setItem(STORAGE_KEY, result.token)
    } catch {
      // The session still works for this tab.
    }
  }, [])

  const signOut = useCallback(() => {
    setToken(null)
    setEmail(null)
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Nothing to do.
    }
  }, [])

  return { token, email, isChecking, signIn, signOut }
}
